import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import { fetchJson$ } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import {
	categoryGuidToString,
	type Category,
	type Categories,
	type CategoryGuid,
} from '../../../types';
import { APOLLO_STARGATE_CONTEXT_ROOT } from '../constants';

export type CategoriesResponse = {
	categories: Categories;
	categoriesSupported: boolean;
};

export const listCategories = (
	projectId: number,
	activationId: string,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<CategoriesResponse> => {
	const categoryPath = `api/project/${projectId}/category`;
	return fetchJson$(`${apolloPath}/${activationId}/${categoryPath}`);
};

export const deleteCategory = (
	projectId: number,
	categoryId: CategoryGuid,
	activationId: string,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Observable<any> => {
	const categoryPath = `api/project/${projectId}/category/`;
	return fetchJson$(
		`${apolloPath}/${activationId}/${categoryPath}${categoryGuidToString(categoryId)}`,
		{
			method: 'DELETE',
		},
	);
};

export type CreateCategoryRequest = {
	name: string;
	description: string;
};

type FieldErrorsResponse = {
	[field: string]: {
		errorKey: string;
		errorMessage: string;
	};
};

export type ApolloErrorResponse = {
	statusCode: number;
	errorKey?: string;
	errorMessage?: string;
	fieldErrors?: FieldErrorsResponse;
};

export const createCategory = (
	projectId: number,
	activationId: string,
	payload: CreateCategoryRequest,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<Category> => {
	const categoryPath = `api/project/${projectId}/category`;
	return fetchJson$(`${apolloPath}/${activationId}/${categoryPath}`, {
		method: 'POST',
		body: JSON.stringify(payload),
	}).catch(({ response }) => Observable.throw(response));
};
export type CategoryArticleRequest = {
	projectId: number;
	categoryId: CategoryGuid;
	articleId: string;
	isFeatured?: boolean;
	apolloPath?: string;
	spaceKey: string;
	activationId: string;
};

export const addArticleToCategory = ({
	projectId,
	categoryId,
	articleId,
	isFeatured,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: CategoryArticleRequest): Observable<void> => {
	let body = {
		articleId,
		featured: isFeatured,
	};

	// @ts-expect-error - TS2322 - Type '{ spaceKey: string; articleId: string; featured: boolean | undefined; }' is not assignable to type '{ articleId: string; featured: boolean | undefined; }'.
	body = { ...body, spaceKey };

	return fetchJson$(
		`${apolloPath}/${activationId}/api/project/${projectId}/category/${categoryGuidToString(
			categoryId,
		)}/article`,
		{
			method: 'POST',
			body: JSON.stringify(body),
		},
	);
};

export const removeArticleFromCategory = ({
	projectId,
	categoryId,
	articleId,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: CategoryArticleRequest): Observable<void> =>
	fetchJson$(
		`${apolloPath}/${activationId}/api/project/${projectId}/category/${categoryGuidToString(
			categoryId,
		)}/spaceKey/${spaceKey}/article/${articleId}`,
		{
			method: 'DELETE',
		},
	);

export type UpdateCategoryRequest = {
	name?: string;
	description?: string;
};

export const updateCategory = (
	projectId: number,
	categoryId: CategoryGuid,
	activationId: string,
	payload: UpdateCategoryRequest,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<Category> =>
	fetchJson$(
		`${apolloPath}/${activationId}/api/project/${projectId}/category/${categoryGuidToString(
			categoryId,
		)}`,
		{
			method: 'PUT',
			body: JSON.stringify(payload),
		},
	).catch(({ response }) => Observable.throw(response));

export type FeatureArticleInCategoryRequest = {
	projectId: number;
	categoryId: CategoryGuid;
	articleId: string;
	isFeatured: boolean;
	spaceKey: string;
	apolloPath?: string;
	activationId: string;
};

export const featureArticleInCategory = ({
	projectId,
	categoryId,
	articleId,
	isFeatured,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: FeatureArticleInCategoryRequest): Observable<void> => {
	let body = {
		featured: isFeatured,
	};

	// @ts-expect-error - TS2322 - Type '{ spaceKey: string; featured: boolean; }' is not assignable to type '{ featured: boolean; }'.
	body = { ...body, spaceKey };

	return fetchJson$(
		`${apolloPath}/${activationId}/api/project/${projectId}/category/${categoryGuidToString(
			categoryId,
		)}/article/${articleId}`,
		{
			method: 'PUT',
			body: JSON.stringify(body),
		},
	).catch(({ response }) => Observable.throw(response));
};

type CategoryIdWithOrder = {
	categoryId: CategoryGuid;
	order: number;
};

export type OrderCategoriesRequest = {
	projectId: number;
	activationId: string;
	categories: CategoryIdWithOrder[];
	apolloPath?: string;
};

type CategoryResponse = {
	id: CategoryGuid;
	name: string;
	description?: string;
	categoryUrl: string;
};

type CategoryReorderResponse = {
	categories: CategoryResponse[];
	errors: string[];
};

export const orderCategories = ({
	projectId,
	categories,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: OrderCategoriesRequest): Observable<CategoryReorderResponse> =>
	fetchJson$(`${apolloPath}/${activationId}/api/project/${projectId}/category/order`, {
		method: 'PUT',
		body: JSON.stringify(categories),
	}).catch(({ response }) => Observable.throw(response));
